import React, { memo, useState, useEffect, useCallback } from 'react';
import { Editor } from '@tiptap/react';
import {
  Heading1, Heading2, Heading3, Pilcrow, Bold, Italic,
  List, ListOrdered, Minus, Copy, Maximize2, Check,
  Undo2, Redo2
} from 'lucide-react';

interface FloatingToolbarProps {
  editor: Editor | null;
  onFullscreen?: () => void;
  copied?: boolean;
  onCopy?: () => void;
  className?: string;
  showWordCount?: boolean;
  showFullscreen?: boolean;
}

const TOOLBAR_ITEMS = [
  { type: 'undo', icon: Undo2, label: '撤销' },
  { type: 'redo', icon: Redo2, label: '重做' },
  { type: 'separator' },
  { type: 'heading', level: 1, icon: Heading1, label: 'H1' },
  { type: 'heading', level: 2, icon: Heading2, label: 'H2' },
  { type: 'heading', level: 3, icon: Heading3, label: 'H3' },
  { type: 'paragraph', icon: Pilcrow, label: '正文' },
  { type: 'separator' },
  { type: 'bold', icon: Bold, label: '加粗' },
  { type: 'italic', icon: Italic, label: '斜体' },
  { type: 'separator' },
  { type: 'bulletList', icon: List, label: '无序列表' },
  { type: 'orderedList', icon: ListOrdered, label: '有序列表' },
  { type: 'separator' },
  { type: 'horizontalRule', icon: Minus, label: '分割线' },
  { type: 'separator' },
  { type: 'copy', icon: Copy, label: '复制' },
  { type: 'fullscreen', icon: Maximize2, label: '全屏' },
] as const;

const FloatingToolbarInner: React.FC<FloatingToolbarProps> = ({
  editor,
  onFullscreen,
  copied,
  onCopy,
  className = '',
  showWordCount = false,
  showFullscreen = true,
}) => {
  const [localCopied, setLocalCopied] = useState(false);
  const [wordCount, setWordCount] = useState(0);

  // 更新字数统计（统计非空白字符数，即"字数"）
  useEffect(() => {
    if (!editor || !showWordCount) return;
    const updateWordCount = () => {
      const text = editor.getText();
      const charCount = text.replace(/\s/g, '').length;
      setWordCount(charCount);
    };
    updateWordCount();
    editor.on('update', updateWordCount);
    return () => {
      editor.off('update', updateWordCount);
    };
  }, [editor, showWordCount]);

  const handleCopy = useCallback(() => {
    if (onCopy) {
      onCopy();
    } else {
      const text = editor?.getText() || '';
      navigator.clipboard.writeText(text);
      setLocalCopied(true);
      setTimeout(() => setLocalCopied(false), 2000);
    }
  }, [editor, onCopy]);

  const handleUndo = useCallback(() => {
    editor?.chain().focus().undo().run();
  }, [editor]);

  const handleRedo = useCallback(() => {
    editor?.chain().focus().redo().run();
  }, [editor]);

  const handleHeading = (level: number) => {
    editor?.chain().focus().toggleHeading({ level: level as 1 | 2 | 3 }).run();
  };

  const handleParagraph = () => {
    editor?.chain().focus().setParagraph().run();
  };

  const handleBold = () => {
    editor?.chain().focus().toggleBold().run();
  };

  const handleItalic = () => {
    editor?.chain().focus().toggleItalic().run();
  };

  const handleBulletList = () => {
    editor?.chain().focus().toggleBulletList().run();
  };

  const handleOrderedList = () => {
    editor?.chain().focus().toggleOrderedList().run();
  };

  const handleHorizontalRule = () => {
    editor?.chain().focus().setHorizontalRule().run();
  };

  const isActive = (type: string, level?: number) => {
    if (!editor) return false;
    if (type === 'heading' && level) {
      return editor.isActive('heading', { level });
    }
    if (type === 'paragraph') return false; // 正文按钮不高亮，始终显示为普通状态
    if (type === 'bold') return editor.isActive('bold');
    if (type === 'italic') return editor.isActive('italic');
    if (type === 'bulletList') return editor.isActive('bulletList');
    if (type === 'orderedList') return editor.isActive('orderedList');
    return false;
  };

  const canUndo = editor?.can().undo() ?? false;
  const canRedo = editor?.can().redo() ?? false;

  const displayCopied = copied || localCopied;

  // 过滤工具栏项：全屏模式下隐藏全屏按钮
  const toolbarItems = showFullscreen
    ? TOOLBAR_ITEMS
    : TOOLBAR_ITEMS.filter(item => item.type !== 'fullscreen');

  return (
    <div
      className={`floating-toolbar nodrag flex items-center gap-[6px] bg-[#262626]/95 backdrop-blur-xl border border-white/10 rounded-xl px-3 py-1.5 shadow-lg max-w-fit ${className}`}
    >
      {toolbarItems.map((item, index) => {
        if (item.type === 'separator') {
          return (
            <div
              key={`sep-${index}`}
              className="w-px h-5 bg-white/10 mx-1"
            />
          );
        }

        const Icon = item.icon;
        const active = isActive(item.type, 'level' in item ? item.level : undefined);

        if (item.type === 'undo') {
          return (
            <button
              key={item.type}
              onClick={handleUndo}
              onMouseDown={(e) => e.stopPropagation()}
              disabled={!canUndo}
              className={`flex items-center justify-center w-9 h-9 rounded-md transition-all cursor-pointer ${
                canUndo
                  ? 'bg-transparent text-white hover:text-white hover:bg-white/10'
                  : 'bg-transparent text-white/20 cursor-not-allowed'
              }`}
              title={item.label}
            >
              <Icon size={16} />
            </button>
          );
        }

        if (item.type === 'redo') {
          return (
            <button
              key={item.type}
              onClick={handleRedo}
              onMouseDown={(e) => e.stopPropagation()}
              disabled={!canRedo}
              className={`flex items-center justify-center w-9 h-9 rounded-md transition-all cursor-pointer ${
                canRedo
                  ? 'bg-transparent text-white hover:text-white hover:bg-white/10'
                  : 'bg-transparent text-white/20 cursor-not-allowed'
              }`}
              title={item.label}
            >
              <Icon size={16} />
            </button>
          );
        }

        if (item.type === 'copy') {
          return (
            <button
              key={item.type}
              onClick={handleCopy}
              onMouseDown={(e) => e.stopPropagation()}
              className="flex items-center justify-center w-9 h-9 rounded-md bg-transparent text-white hover:text-white hover:bg-white/10 transition-all cursor-pointer"
              title={item.label}
            >
              {displayCopied ? (
                <Check size={16} className="text-green-400" />
              ) : (
                <Icon size={16} className="text-white" />
              )}
            </button>
          );
        }

        if (item.type === 'fullscreen') {
          return (
            <button
              key={item.type}
              onClick={onFullscreen}
              onMouseDown={(e) => e.stopPropagation()}
              className="flex items-center justify-center w-9 h-9 rounded-md bg-transparent text-white hover:text-white hover:bg-white/10 transition-all cursor-pointer"
              title={item.label}
            >
              <Icon size={16} className="text-white" />
            </button>
          );
        }

        const handleClick = (e: React.MouseEvent) => {
          e.stopPropagation();
          if (item.type === 'heading' && 'level' in item) handleHeading(item.level as 1 | 2 | 3);
          else if (item.type === 'paragraph') handleParagraph();
          else if (item.type === 'bold') handleBold();
          else if (item.type === 'italic') handleItalic();
          else if (item.type === 'bulletList') handleBulletList();
          else if (item.type === 'orderedList') handleOrderedList();
          else if (item.type === 'horizontalRule') handleHorizontalRule();
        };

        const itemKey = 'level' in item ? `${item.type}-${item.level}` : item.type;

        return (
          <button
            key={itemKey}
            onClick={handleClick}
            onMouseDown={(e) => e.stopPropagation()}
            className={`flex items-center gap-[3px] h-9 px-2.5 rounded-md border-0 transition-all cursor-pointer ${
              active
                ? 'bg-white/15 text-white'
                : 'bg-transparent text-white hover:text-white hover:bg-white/10'
            }`}
            title={item.label}
          >
            {item.type === 'heading' && 'level' in item ? (
              <span className="text-sm font-medium">H{item.level}</span>
            ) : (
              <Icon size={16} />
            )}
            {item.type !== 'heading' && item.type !== 'paragraph' && item.type !== 'horizontalRule' && (
              <span className="text-sm font-medium whitespace-nowrap">{item.label}</span>
            )}
          </button>
        );
      })}
      {showWordCount && (
        <>
          <div className="w-px h-5 bg-white/10 mx-1" />
          <span className="text-xs text-white/40 whitespace-nowrap">
            {wordCount} 字
          </span>
        </>
      )}
    </div>
  );
};

export const FloatingToolbar = memo(FloatingToolbarInner, (prev, next) => {
  if (prev.editor !== next.editor) return false;
  if (prev.copied !== next.copied) return false;
  if (prev.onFullscreen !== next.onFullscreen) return false;
  if (prev.onCopy !== next.onCopy) return false;
  if (prev.className !== next.className) return false;
  if (prev.showWordCount !== next.showWordCount) return false;
  if (prev.showFullscreen !== next.showFullscreen) return false;
  return true;
});
